import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { motion } from "framer-motion";
import { KeyRound, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { useI18n } from "@/hooks/use-i18n";
import { HeaderBar } from "@/components/HeaderBar";
import { Footer } from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { trackCtaVariantConversionComplete } from "@/lib/uxTracking";

export default function Access() {
  const { t, dir, lang, setLang } = useI18n();
  const [, setLocation] = useLocation();
  const [code, setCode] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "valid" | "invalid">("idle");

  useEffect(() => {
    document.title = `${t("access.title")} - Aurelian Private Care`;
  }, [t, lang]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;

    setStatus("loading");
    try {
      const res = await fetch("/api/aurelian/validate-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ access_code: code.trim() }),
      });
      const data = await res.json();

      if (data.valid) {
        setStatus("valid");
        localStorage.setItem("aurelian_access_code", code.trim());
        trackCtaVariantConversionComplete();
        setTimeout(() => setLocation("/evaluation"), 1500);
      } else {
        setStatus("invalid");
      }
    } catch {
      setStatus("invalid");
    }
  };

  return (
    <div className="flex flex-col min-h-screen" dir={dir}>
      <HeaderBar lang={lang} onChangeLang={setLang} dir={dir} />

      <main className="flex-1 flex items-center justify-center py-20 px-6">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/3 left-1/3 w-[500px] h-[500px] rounded-full bg-primary/[0.03] blur-[120px] animate-aurora-drift" />
          <div className="absolute bottom-1/3 right-1/3 w-[400px] h-[400px] rounded-full bg-primary/[0.04] blur-[100px] animate-aurora-drift [animation-direction:reverse]" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md relative"
        >
          <Card className="bg-card/50 backdrop-blur-xl border-border/30 shadow-lg">
            <CardContent className="pt-10 pb-10 px-8">
              <div className="text-center mb-8">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-5">
                  <KeyRound className="w-6 h-6 text-primary" />
                </div>
                <h1
                  className="text-2xl font-serif font-bold text-foreground mb-2"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                  data-testid="text-access-title"
                >
                  {t("access.title")}
                </h1>
                <p className="text-sm text-muted-foreground" data-testid="text-access-subtitle">
                  {t("access.subtitle")}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  type="text"
                  value={code}
                  onChange={(e) => { setCode(e.target.value); setStatus("idle"); }}
                  placeholder={t("access.placeholder")}
                  className="text-center tracking-widest uppercase font-mono"
                  autoFocus
                  data-testid="input-access-code"
                />

                <Button
                  type="submit"
                  className="w-full"
                  disabled={!code.trim() || status === "loading"}
                  data-testid="button-validate-code"
                >
                  {status === "loading" ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    t("access.validate")
                  )}
                </Button>
              </form>

              {status === "valid" && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-5 flex items-center gap-2 text-sm text-green-600 dark:text-green-400 justify-center"
                  data-testid="text-access-valid"
                >
                  <CheckCircle className="w-4 h-4" />
                  <span>{t("access.valid")}</span>
                </motion.div>
              )}

              {status === "invalid" && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-5 flex items-center gap-2 text-sm text-destructive justify-center"
                  data-testid="text-access-invalid"
                >
                  <XCircle className="w-4 h-4" />
                  <span>{t("access.invalid")}</span>
                </motion.div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </main>

      <Footer t={t} dir={dir} />
    </div>
  );
}
